import posthog, { type CaptureResult } from "posthog-js"

import { analytics } from "@/lib/analytics/client"
import { isPostHogEnabled } from "@/lib/analytics/enabled"
import { isIgnoredErrorMessage } from "@/lib/analytics/ignored-errors"
import { reportPerformanceOnLoad } from "@/lib/analytics/performance"

/**
 * `capture_exceptions` patches window.onerror inside posthog-js, so exceptions
 * reach the network without ever passing through GlobalErrorHandler's filter.
 * This hook applies the same list on the SDK's own path.
 *
 * Runs on every event, not just exceptions — anything that is not an
 * `$exception` must fall through untouched, or pageviews get dropped. An
 * exception is discarded only when every entry in the chain is noise, so a
 * real error with a noisy cause is still reported.
 */
function dropIgnoredExceptions(event: CaptureResult | null) {
  if (!event || event.event !== "$exception") return event
  const chain = event.properties?.$exception_list
  if (!Array.isArray(chain) || chain.length === 0) return event
  const allIgnored = chain.every((entry) =>
    isIgnoredErrorMessage(String(entry?.value ?? ""))
  )
  return allIgnored ? null : event
}

// Boot posthog-js only where enabled (NEXT_PUBLIC_POSTHOG_ENABLED=true, set in
// Vercel Production only). Everywhere else it's never init'd, so the analytics
// client SSOT falls back to the console/noop provider.
if (isPostHogEnabled()) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN!, {
    api_host: "/ingest",
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    defaults: "2026-05-30",
    capture_exceptions: true,
    before_send: dropIgnoredExceptions,
    debug: process.env.NODE_ENV === "development",
  })
}

// After init, so the event reaches PostHog. Once per document: App Router
// transitions don't reload it, and the load timings they would re-read are
// the same ones.
reportPerformanceOnLoad((properties) =>
  analytics.capture("app_performance", properties)
)
